import React, { useRef, useState, useEffect, useContext } from "react";
import {
  Modal,
  Spinner,
  Form,
  Button,
  Row,
  Col,
  Dropdown,
  Card,
} from "react-bootstrap";

import { useForm, Controller } from "react-hook-form";
import Swal from "sweetalert2";
import {
  IconDotsVertical,
  IconUserPlus,
  IconPlayerPlay,
  IconPlayerStop,
  IconCheck,
  IconBrain,
} from "@tabler/icons-react";

import { ChangeBotStatus } from "../../../services/WaBottyServices";
import { CONTACTS_URL, COLUMNS_URL, AI_URL } from "../../../api/Urls";
import { api } from "../../../api/Middleware";
import { LABEL_COLORS } from "../ContactsBoard/label-utils";
import { ContactLabel } from "../ContactsBoard/Label";
import Select from "react-select";
import { BotControlsWrapper } from "./styles";
import UserContext from "../../../contexts/UserContext";
import { GetContact } from "../../../services/ContactServices";
import NotificationsContext from "../../../contexts/NotificationsContext";
import { MoonLoader } from "react-spinners";

const BotControls = ({
  bottyData,
  contact,
  phone,
  status,
  onStatusChange,
  setConversations,
  setSelectedConversation,
  selectedConversation,
}) => {
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [showModal, setShowModal] = useState(false);
  const [columns, setColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState(LABEL_COLORS[0]);
  const [contactData, setContactData] = useState(null);
  const [awaitAiResponse, setAwaitAiResponse] = useState(false);
  const btnSubmitRef = useRef(null);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm();

  const watchedLabel = watch("label");

  // Função para mapear dados da API para o formulário
  const mapApiDataToForm = (apiData) => {
    const formData = {
      name: apiData.name || "",
      conversation_history: apiData.conversation_history || "",
      conversation_history_ai: apiData.conversation_history_ai || "",
      is_shared: apiData.is_shared || false,
      label: apiData.label || null,
    };

    // Mapear a coluna da API (número) para o formato do Select
    if (apiData.column && columns.length > 0) {
      const selectedColumn = columns.find(
        (col) => col.value === apiData.column
      );
      if (selectedColumn) {
        formData.column = selectedColumn;
      }
    }

    return formData;
  };

  // Carregamento dos dados do contato da API
  useEffect(() => {
    if (contact && showModal) {
      GetContact(contact)
        .then((res) => {
          if (res) {
            setContactData(res);

            // Se já temos as colunas carregadas, mapear os dados imediatamente
            if (columns.length > 0) {
              const formData = mapApiDataToForm(res);
              reset(formData);

              // Configurar label se existir
              if (res.label) {
                setLabelName(res.label.name || "");
                setLabelColor(res.label.color || LABEL_COLORS[0]);
              } else {
                setLabelName("");
                setLabelColor(LABEL_COLORS[0]);
              }
            }
          }
        })
        .catch((err) => {
          console.error("Erro ao carregar contato:", err);
          Swal.fire(
            "Erro",
            "Não foi possível carregar os dados do contato.",
            "error"
          );
        });
    }
  }, [contact, showModal, reset]);

  // Mapear dados quando as colunas são carregadas e temos dados do contato
  useEffect(() => {
    if (contactData && columns.length > 0) {
      const formData = mapApiDataToForm(contactData);
      reset(formData);

      // Configurar label se existir
      if (contactData.label) {
        setLabelName(contactData.label.name || "");
        setLabelColor(contactData.label.color || LABEL_COLORS[0]);
      }
    }
  }, [contactData, columns, reset]);

  // Fallback para selectedConversation (dados locais)
  useEffect(() => {
    if (!contact && selectedConversation && showModal && columns.length > 0) {
      // Se não há contato da API, usar dados do selectedConversation
      setValue("name", selectedConversation.name || "");
      setValue(
        "conversation_history",
        selectedConversation.conversation_history || ""
      );
      setValue(
        "conversation_history_ai",
        selectedConversation.conversation_history_ai || ""
      );
      setValue("is_shared", selectedConversation.is_shared || false);

      // Mapear coluna se existir
      if (selectedConversation.column) {
        const selectedColumn = columns.find(
          (col) => col.value === selectedConversation.column
        );
        if (selectedColumn) {
          setValue("column", selectedColumn);
        }
      }

      if (selectedConversation.label) {
        setValue("label", selectedConversation.label);
        setLabelName(selectedConversation.label.name || "");
        setLabelColor(selectedConversation.label.color || LABEL_COLORS[0]);
      }
    }
  }, [contact, selectedConversation, showModal, columns, setValue]);

  const handlerPauseOrActive = () => {
    Swal.fire({
      title: "Você tem certeza?",
      html: `Você está prestes a ativar o funcionamento do Agente para este contato`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#515151",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: `Ativar o Agente!`,
    }).then((result) => {
      if (result.isConfirmed) {
        let payload = {
          uid: bottyData.uid,
          phone: phone,
          bot_status: 1,
        };

        ChangeBotStatus(payload, setShowModal, loadColumns).then((result) => {
          if (result) {
            Swal.fire(
              "Sucesso!",
              `Agente ativado para este contato!`,
              "success"
            );
            onStatusChange(1);
          }
        });
      }
    });
  };

  const handlerDisable = () => {
    Swal.fire({
      title: "Você tem certeza?",
      html: "Você está prestes a <b>desativar</b> permanentemente o funcionamento do Agente para este contato.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#515151",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Desativar o Agente!",
    }).then((result) => {
      if (result.isConfirmed) {
        ChangeBotStatus(
          {
            action: "enable_or_disable_bot",
            phone: phone,
            uid: bottyData.uid,
            bot_status: 0,
          },
          setShowModal,
          loadColumns
        )
          .then((result) => {
            if (result) {
              Swal.fire(
                "Sucesso!",
                "Agente desativado para este contato!",
                "success"
              );
              onStatusChange(0);
            }
          })
          .catch((error) => {
            console.log(error.response);
            Swal.fire(
              "Oops!",
              "Ocorreu um erro ao desativar o Agente, por favor, tente novamente.",
              "error"
            );
          });
      }
    });
  };

  const loadColumns = async () => {
    setLoadingColumns(true);
    try {
      const response = await api.get(`${COLUMNS_URL}`);
      if (response.data && response.data.results) {
        const columnOptions = response.data.results.map((column) => ({
          value: column.id,
          label: column.name,
        }));
        setColumns(columnOptions);
      }
    } catch (err) {
      console.error("Erro ao carregar colunas:", err);
      Swal.fire("Erro", "Não foi possível carregar as colunas.", "error");
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleGenerateSummary = async () => {
    setAwaitAiResponse(true);
    try {
      const response = await api.post(AI_URL + `generate_summary/`, {
        uid: bottyData?.uid,
        phone: phone,
      });

      setValue("conversation_history_ai", response.data);

      setNotify({
        type: "success",
        message: "Resumo gerado com sucesso!",
        title: "Sucesso",
      });
    } catch (err) {
      console.error("Erro ao gerar resumo:", err);
      setNotify({
        type: "Oops!",
        message: "Não foi possível gerar o resumo",
        title: "error",
      });
    } finally {
      setAwaitAiResponse(false);
    }
  };

  const handleLabelChange = () => {
    if (labelName.trim() && labelName.length <= 12) {
      const newLabel = {
        name: labelName.trim(),
        color: labelColor,
      };
      setValue("label", newLabel);
    } else {
      setValue("label", null);
    }
  };

  useEffect(() => {
    handleLabelChange();
  }, [labelName, labelColor]);

  const onSubmit = async (data) => {
    btnSubmitRef.current.setAttribute("disabled", true);

    if (!data.column?.value) {
      Swal.fire(
        "Oops!",
        "Por favor, selecione uma coluna válida para continuar!",
        "warning"
      );
      btnSubmitRef.current.removeAttribute("disabled");
      return;
    }
    try {
      const contactPayload = {
        name: data.name,
        phone: phone,
        conversation_history: data.conversation_history || "",
        conversation_history_ai: data.conversation_history_ai || "",
        is_shared: data.is_shared || false,
        column: data.column?.value,
        label: data.label || {},
        last_update: new Date().toISOString(),
        account_id: user?.account_id,
      };

      // Se está editando um contato existente, usar PUT em vez de POST
      let contactResponse;
      if (contact) {
        contactResponse = await api.put(
          `${CONTACTS_URL}${contact}/`,
          contactPayload
        );
      } else {
        contactResponse = await api.post(`${CONTACTS_URL}`, contactPayload);
        if (contactResponse && contactResponse?.id) {
          setSelectedConversation({
            ...selectedConversation,
            contact: contactResponse.id,
          });
        }
      }

      Swal.fire(
        "Sucesso!",
        contact
          ? "Contato atualizado com sucesso!"
          : "Contato criado com sucesso!",
        "success"
      );

      // Atualizar o nome na UI
      const conversationName = document.getElementById(`conversation${phone}`);
      if (conversationName) {
        conversationName.innerText = data.name;
        setSelectedConversation((prev) => ({
          ...prev,
          name: data.name,
          conversation_history: data.conversation_history,
          conversation_history_ai: data.conversation_history_ai,
          is_shared: data.is_shared,
          label: data.label,
        }));

        setConversations((prev) =>
          prev.map((conv) =>
            conv.phone === phone
              ? {
                  ...conv,
                  name: data.name,
                  avatar: `https://ui-avatars.com/api/?name=${data.name}&background=random`,
                  label: data.label,
                }
              : conv
          )
        );
      }

      setShowModal(false);
      reset();
      setContactData(null);
      setLabelName("");
      setLabelColor(LABEL_COLORS[0]);
    } catch (err) {
      console.error("Erro ao salvar contato:", err);
      Swal.fire(
        "Erro",
        contact
          ? "Não foi possível atualizar o contato."
          : "Não foi possível criar o contato.",
        "error"
      );
    } finally {
      if (btnSubmitRef.current) {
        btnSubmitRef.current.removeAttribute("disabled");
      }
    }
  };

  const handleOpenModal = () => {
    setShowModal(true);
    loadColumns();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    reset();
    setContactData(null);
    setLabelName("");
    setLabelColor(LABEL_COLORS[0]);
  };

  return (
    <BotControlsWrapper className="position-relative">
      <Dropdown>
        <Dropdown.Toggle
          variant="outline-secondary"
          size="sm"
          className="border-0 rounded-circle p-2"
          style={{ width: "40px", height: "40px" }}
        >
          <IconDotsVertical size={20} />
        </Dropdown.Toggle>

        <Dropdown.Menu align="end">
          <Dropdown.Item onClick={handleOpenModal}>
            <IconUserPlus size={18} className="me-2" />
            {contact ? "Editar Contato" : "Criar Contato"}
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            onClick={handlerPauseOrActive}
            disabled={status === 1}
            className="text-success"
          >
            <IconPlayerPlay size={18} className="me-2" />
            Ativar IA
          </Dropdown.Item>

          <Dropdown.Item
            onClick={handlerDisable}
            disabled={status !== 1}
            className="text-danger"
          >
            <IconPlayerStop size={18} className="me-2" />
            Desativar IA
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>

      {/* Modal de Contato */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <IconUserPlus size={24} className="me-2" />
            {contact ? "Editar Contato" : "Criar Contato"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            {/* Primeira linha - Nome, Telefone e Coluna */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Nome do Contato *</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("name", { required: "Nome é obrigatório" })}
                    placeholder="Digite o nome do contato"
                    isInvalid={!!errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Telefone</Form.Label>
                  <Form.Control
                    type="text"
                    value={phone}
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Coluna *</Form.Label>
                  {loadingColumns ? (
                    <div className="d-flex align-items-center p-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <span>Carregando colunas...</span>
                    </div>
                  ) : (
                    <Controller
                      name="column"
                      control={control}
                      rules={{ required: "Coluna é obrigatória" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={columns}
                          placeholder="Selecione uma coluna"
                          isSearchable
                          className={errors.column ? "is-invalid" : ""}
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "56px",
                              fontSize: "13px",
                              borderRadius: "1.25rem",
                            }),
                          }}
                        />
                      )}
                    />
                  )}
                  {errors.column && (
                    <div className="invalid-feedback d-block">
                      {errors.column.message}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* FollowUp Humano */}
            <Form.Group className="mb-3">
              <Form.Label>FollowUp Humano</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                {...register("conversation_history")}
                placeholder="FollowUp e anotações do contato..."
              />
            </Form.Group>

            {/* FollowUp IA */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <Form.Label>FollowUp IA</Form.Label>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleGenerateSummary}
                  className="d-flex align-items-center p-2"
                  disabled={awaitAiResponse ? true : false}
                >
                  {awaitAiResponse ? (
                    <>
                      {" "}
                      <MoonLoader
                        className="mr-2"
                        size={16}
                        color="var(--primary)"
                      />
                      Aguarde...
                    </>
                  ) : (
                    <>
                      <IconBrain size={16} className="me-1" />
                      Gerar Resumo com IA
                    </>
                  )}
                </Button>
              </div>

              <Form.Control
                as="textarea"
                rows={4}
                {...register("conversation_history_ai")}
                placeholder="FollowUp da IA..."
                disabled
              />
            </Form.Group>

            {/* Checkbox Compartilhado */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="is_shared"
                label="Compartilhado com a conta"
                {...register("is_shared")}
              />
            </Form.Group>

            {/* Label Section */}
            <Card
              className="mb-3"
              style={{ backgroundColor: "rgba(37, 211, 102, 0.05)" }}
            >
              <Card.Body>
                <Form.Label>Label</Form.Label>
                <Row className="align-items-center">
                  <Col md={4}>
                    <Form.Control
                      type="text"
                      value={labelName}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 12) {
                          setLabelName(value);
                        }
                      }}
                      placeholder="Nome da label (máx. 12 chars)"
                      maxLength={12}
                      size="sm"
                    />
                  </Col>
                  <Col md={8}>
                    <div className="d-flex flex-wrap gap-2">
                      {LABEL_COLORS.map((color) => (
                        <div
                          key={color}
                          style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            backgroundColor: color,
                            cursor: "pointer",
                            border:
                              labelColor === color
                                ? "3px solid #333"
                                : "2px solid transparent",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                          onClick={() => setLabelColor(color)}
                        />
                      ))}
                    </div>
                  </Col>
                </Row>
                {watchedLabel && (
                  <div className="mt-2 d-flex align-items-center">
                    <span className="me-2">Preview:</span>
                    <ContactLabel label={watchedLabel} />
                  </div>
                )}
                <small className="text-muted">
                  {labelName.length}/12 caracteres
                </small>
              </Card.Body>
            </Card>

            {/* Botão Submit */}
            <div className="d-grid">
              <Button
                ref={btnSubmitRef}
                type="submit"
                variant="primary"
                size="lg"
                className="d-flex align-items-center justify-content-center"
              >
                <IconCheck size={18} className="me-2" />
                {contact ? "Atualizar Contato" : "Salvar Contato"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </BotControlsWrapper>
  );
};

export default BotControls;
